import React, { useEffect, useState } from 'react';
import './FeatureStyles.css';
import './PRProcessingFeature.css';

const PRProcessingAnimation = () => {
  const [phase, setPhase] = useState(0);
  // 0: PR diff arrives, 1: AI analyzing, 2: results appear (execute/change/add)

  useEffect(() => {
    const interval = setInterval(() => {
      setPhase(prev => (prev + 1) % 3);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="feature-animation abstract pr-processing-abstract">
      <svg className="abstract-canvas" viewBox="0 0 320 200">
        <defs>
          <linearGradient id="pr-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#ec4899" />
          </linearGradient>
        </defs>

        {/* PR Node (left) */}
        <g className="pr-icon-group">
          <circle
            className={`abstract-node ${phase >= 0 ? 'blue active' : 'gray'}`}
            cx="50"
            cy="100"
            r="22"
          />
          <text x="50" y="96" textAnchor="middle" fill="white" fontSize="9" fontWeight="600">PR</text>
          <text x="50" y="108" textAnchor="middle" fill="white" fontSize="7" opacity="0.8">#42</text>
        </g>

        {/* Connection line: PR → AI */}
        <path
          className={`abstract-line ${phase >= 1 ? 'blue' : 'gray'}`}
          d="M 72 100 L 138 100"
        />

        {/* Traveling particle (phase 0) */}
        {phase === 0 && (
          <circle className="pr-flow-particle" r="4">
            <animateMotion dur="2s" repeatCount="indefinite" path="M 72 100 L 138 100" />
          </circle>
        )}

        {/* AI Analysis Node (center) */}
        <g>
          {phase === 1 && (
            <circle className="pr-node-pulse" cx="160" cy="100" r="18" fill="#3b82f6" />
          )}
          <circle
            className={`abstract-node ${phase >= 1 ? 'blue active' : 'gray'}`}
            cx="160"
            cy="100"
            r="18"
          />
          {phase === 1 ? (
            <g>
              <circle cx="153" cy="100" r="2" fill="white" opacity="0.8">
                <animate attributeName="opacity" values="0.3;1;0.3" dur="0.8s" repeatCount="indefinite" />
              </circle>
              <circle cx="160" cy="100" r="2" fill="white" opacity="0.8">
                <animate attributeName="opacity" values="0.3;1;0.3" dur="0.8s" begin="0.2s" repeatCount="indefinite" />
              </circle>
              <circle cx="167" cy="100" r="2" fill="white" opacity="0.8">
                <animate attributeName="opacity" values="0.3;1;0.3" dur="0.8s" begin="0.4s" repeatCount="indefinite" />
              </circle>
            </g>
          ) : (
            <text x="160" y="103" textAnchor="middle" fill="white" fontSize="8" fontWeight="600">AI</text>
          )}
        </g>

        {/* Result branches (phase 2+) */}
        {phase >= 2 && (
          <g className="pr-result-fade">
            {/* Branch lines from AI to three results */}
            <path className="abstract-line blue pr-result-line" d="M 178 92 L 240 55" />
            <path className="abstract-line blue pr-result-line" d="M 178 100 L 240 100" style={{ animationDelay: '0.15s' }} />
            <path className="abstract-line blue pr-result-line" d="M 178 108 L 240 145" style={{ animationDelay: '0.3s' }} />

            {/* Execute node (top-right) */}
            <g className="pr-result-fade" style={{ animationDelay: '0.3s' }}>
              <circle cx="260" cy="50" r="20" fill="#3b82f6" opacity="0.15" />
              <circle cx="260" cy="50" r="16" fill="#3b82f6" />
              <text className="pr-category-label" x="260" y="47" textAnchor="middle" fill="white">Run</text>
              <text className="pr-result-count" x="260" y="57" textAnchor="middle" fill="white">5</text>
            </g>

            {/* Change node (mid-right) */}
            <g className="pr-result-fade" style={{ animationDelay: '0.45s' }}>
              <circle cx="262" cy="100" r="20" fill="#ec4899" opacity="0.15" />
              <circle cx="262" cy="100" r="16" fill="#ec4899" />
              <text className="pr-category-label" x="262" y="97" textAnchor="middle" fill="white">Fix</text>
              <text className="pr-result-count" x="262" y="107" textAnchor="middle" fill="white">2</text>
            </g>

            {/* Add node (bottom-right) */}
            <g className="pr-result-fade" style={{ animationDelay: '0.6s' }}>
              <circle cx="260" cy="150" r="20" fill="#3b82f6" opacity="0.15" />
              <circle cx="260" cy="150" r="16" fill="#3b82f6" />
              <text className="pr-category-label" x="260" y="147" textAnchor="middle" fill="white">New</text>
              <text className="pr-result-count" x="260" y="157" textAnchor="middle" fill="white">1</text>
            </g>
          </g>
        )}
      </svg>
    </div>
  );
};

const PRProcessingFeature = {
  icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M6 3v12" />
      <circle cx="18" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <path d="M18 9a9 9 0 0 1-9 9" />
    </svg>
  ),
  title: "Smart PR / MR Processing",
  description: "Push a pull request or merge request, let AI decide what to test. Qate connects to GitHub, GitLab, and Bitbucket, analyzes your diff against the codebase map, and selects which tests to execute, which need changes, and what new tests to create.",
  highlight: [
    <><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ verticalAlign: '-2px', marginRight: '4px' }}><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>GitHub</>,
    <><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ verticalAlign: '-2px', marginRight: '4px' }}><path d="M22.65 14.39L12 22.13.35 14.39c-.18-.14-.25-.37-.18-.58L2.27 8.6l2.1 6.45h15.25l2.1-6.45 2.11 5.21c.07.21 0 .44-.18.58z"/><path d="M12 22.13L17.62 15.05H6.38L12 22.13z"/><path d="M6.38 15.05L2.27 8.6 12 22.13 6.38 15.05z"/><path d="M2.27 8.6l-.92 2.83c-.08.26.01.54.23.71L12 22.13 2.27 8.6z"/><path d="M2.27 8.6h4.11L4.38 2.55c-.1-.3-.52-.3-.62 0L2.27 8.6z"/><path d="M17.62 15.05L21.73 8.6 12 22.13l5.62-7.08z"/><path d="M21.73 8.6l.92 2.83c.08.26-.01.54-.23.71L12 22.13 21.73 8.6z"/><path d="M21.73 8.6h-4.11l1.99-6.05c.1-.3.52-.3.62 0L21.73 8.6z"/></svg>GitLab</>,
    <><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ verticalAlign: '-2px', marginRight: '4px' }}><path d="M.778 1.211a.768.768 0 00-.768.892l3.263 19.81c.084.5.515.868 1.022.873H19.95a.772.772 0 00.77-.646l3.27-20.03a.768.768 0 00-.768-.891L.778 1.211zM14.52 15.53H9.522L8.17 8.466h7.561l-1.211 7.064z"/></svg>Bitbucket</>
  ],
  animation: <PRProcessingAnimation />,
  docsLink: { url: '/documentation#web-cicd', label: 'CI Integration Docs' }
};

export default PRProcessingFeature;
